// src/contexts/AuthContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';

interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  avatar_url: string;
  bio?: string;
  oab_number?: string;
  phone?: string;
  specialization?: string[];
  address?: any;
  preferences?: any;
  created_at: string;
  updated_at: string;
  role?: 'CEO' | 'Advogado' | 'Secretário(a)' | 'Estagiário';
  status?: 'PENDING' | 'ACTIVE' | 'INACTIVE';
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const getSessionData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSessionData();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Dentro do AuthContext.tsx, encontre este useEffect e corrija a linha do .eq()

  const loadProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();
    if (error && error.code !== 'PGRST116') { // Ignora erro se o perfil ainda não existe
      console.error("Erro ao buscar perfil:", error);
    }
    if (data) {
      setProfile(data as Profile);
    }
  };

  useEffect(() => {
    // Se temos um usuário logado, buscamos seu perfil
    if (user) loadProfile(user.id);
  }, [user]);

  const refreshProfile = async () => {
    if (user) await loadProfile(user.id);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    navigate('/auth');
  };

  const value = {
    session,
    user,
    profile,
    loading,
    logout,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};